import { useEffect, useState } from 'react';
import { Button } from '../components/Button';
import { adminFetch } from '../lib/api';

type Summary = {
  counts: Record<string, number>;
  recentReports: Array<{ id: string; origin_city: string; destination_city: string; created_at: string; status: string }>;
};

type Place = { id?: string; name: string; city_id?: string; type: string; description: string; tags: string; source_url: string; verification_status: string; };

type City = { id: string; name: string; country: string };

export function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('uncle-honest-admin') || '');
  const [input, setInput] = useState('');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [place, setPlace] = useState<Place>({ name: '', type: 'place', description: '', tags: '', source_url: '', verification_status: 'needs_review' });
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) return;
    localStorage.setItem('uncle-honest-admin', token);
    Promise.all([
      adminFetch<Summary>('/summary', token),
      adminFetch<{ cities: City[] }>('/cities', token)
    ]).then(([s, c]) => { setSummary(s); setCities(c.cities); }).catch(() => setMessage('Admin token failed.'));
  }, [token]);

  async function savePlace() {
    setMessage('');
    try {
      await adminFetch('/places', token, { method: 'POST', body: JSON.stringify({ ...place, tags: place.tags.split(',').map((x) => x.trim()).filter(Boolean) }) });
      setMessage('Place saved.');
      setPlace({ name: '', type: 'place', description: '', tags: '', source_url: '', verification_status: 'needs_review' });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  if (!token) {
    return (
      <main className="mx-auto max-w-xl px-5 py-16">
        <h1 className="font-serif text-6xl tracking-[-0.06em]">admin</h1>
        <p className="mt-4 text-charcoal/65">Enter the ADMIN_SECRET set in Cloudflare Pages environment variables.</p>
        <input className="focus-ring mt-8 w-full rounded-full border border-charcoal/10 bg-warm px-5 py-3" type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="admin secret" />
        <Button className="mt-4" onClick={() => setToken(input)}>Enter</Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-5 py-12 md:px-8">
      <div className="flex items-center justify-between gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal/45">secure dashboard</p>
          <h1 className="font-serif text-6xl tracking-[-0.06em]">admin</h1>
        </div>
        <Button variant="secondary" onClick={() => { localStorage.removeItem('uncle-honest-admin'); setToken(''); }}>Sign out</Button>
      </div>

      {summary && <section className="mt-10 grid gap-3 md:grid-cols-4">{Object.entries(summary.counts).map(([k, v]) => <div key={k} className="rounded-[2rem] border border-charcoal/10 bg-warm p-6"><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/45">{k}</p><p className="mt-4 font-serif text-5xl tracking-[-0.05em]">{v}</p></div>)}</section>}

      <section className="mt-10 grid gap-8 md:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[2rem] border border-charcoal/10 bg-warm p-6">
          <h2 className="font-serif text-4xl tracking-[-0.05em]">add curated place</h2>
          <div className="mt-6 grid gap-3">
            <input className="focus-ring rounded-2xl border border-charcoal/10 bg-bone px-4 py-3" placeholder="name" value={place.name} onChange={(e) => setPlace({ ...place, name: e.target.value })} />
            <select className="focus-ring rounded-2xl border border-charcoal/10 bg-bone px-4 py-3" value={place.city_id || ''} onChange={(e) => setPlace({ ...place, city_id: e.target.value })}><option value="">city</option>{cities.map((c) => <option key={c.id} value={c.id}>{c.name}, {c.country}</option>)}</select>
            <input className="focus-ring rounded-2xl border border-charcoal/10 bg-bone px-4 py-3" placeholder="type e.g. bookstore, museum, market" value={place.type} onChange={(e) => setPlace({ ...place, type: e.target.value })} />
            <textarea className="focus-ring min-h-32 rounded-2xl border border-charcoal/10 bg-bone px-4 py-3" placeholder="description" value={place.description} onChange={(e) => setPlace({ ...place, description: e.target.value })} />
            <input className="focus-ring rounded-2xl border border-charcoal/10 bg-bone px-4 py-3" placeholder="tags comma separated" value={place.tags} onChange={(e) => setPlace({ ...place, tags: e.target.value })} />
            <input className="focus-ring rounded-2xl border border-charcoal/10 bg-bone px-4 py-3" placeholder="source url" value={place.source_url} onChange={(e) => setPlace({ ...place, source_url: e.target.value })} />
            <Button onClick={savePlace} disabled={!place.name || !place.city_id}>Save place</Button>
            {message && <p className="text-sm text-charcoal/65">{message}</p>}
          </div>
        </div>
        <div className="rounded-[2rem] border border-charcoal/10 bg-warm p-6">
          <h2 className="font-serif text-4xl tracking-[-0.05em]">recent reports</h2>
          <div className="mt-5 grid gap-2">
            {summary?.recentReports?.map((r) => <div key={r.id} className="rounded-2xl bg-bone p-4"><p className="font-semibold">{r.origin_city} → {r.destination_city}</p><p className="text-xs text-charcoal/50">{r.status} · {new Date(r.created_at).toLocaleString()}</p></div>)}
          </div>
        </div>
      </section>
    </main>
  );
}
